import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { supabase } from "../../supabaseClient";
import TaskAssistantChat from "./TaskAssistantChat";
import ChatAssistantInput from "./ChatAssistantInput";
import PortalDropdown from "./PortalDropdown";
import QuickIdeaPopup from "./QuickIdeaPopup";
import ConfirmationPopup from "./ConfirmationPopup";
import Cookies from "js-cookie";
import "../../assets/css/checklist.css";

function stripHtml(value) {
  if (!value) return "";
  if (typeof window !== "undefined") {
    const parsed = new window.DOMParser().parseFromString(value, "text/html");
    return parsed.body.textContent || "";
  }
  return value.replace(/<[^>]*>/g, " ");
}

function formatIdeaDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const IdeasExpandAllPopup = ({ show, handleClose, userId, isDarkMode }) => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatWidthPercent, setChatWidthPercent] = useState(60);
  const [isResizing, setIsResizing] = useState(false);
  const [showAddIdeaModal, setShowAddIdeaModal] = useState(false);
  const [ideaToEdit, setIdeaToEdit] = useState(null);
  const [ideaToDelete, setIdeaToDelete] = useState(null);
  const [isDeletingIdea, setIsDeletingIdea] = useState(false);
  const chatAssistantRef = useRef(null);
  const modalBodyRef = useRef(null);

  const currentSessionId = Cookies.get("chat_session_id");
  const normalizedSessionId =
    currentSessionId && currentSessionId !== "undefined" ? currentSessionId : null;

  const fetchIdeas = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchError } = await supabase
        .from("quick_ideas")
        .select("id, title, description, tags, attachment_urls, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setIdeas(data || []);
    } catch (err) {
      setError(err?.message || "Failed to load ideas.");
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (show) fetchIdeas();
  }, [show, fetchIdeas]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => fetchIdeas();
    window.addEventListener("quick-ideas:changed", handler);
    return () => window.removeEventListener("quick-ideas:changed", handler);
  }, [fetchIdeas]);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizing || !modalBodyRef.current) return;
      const rect = modalBodyRef.current.getBoundingClientRect();
      const chatWidth = rect.width - (e.clientX - rect.left);
      let pct = (chatWidth / rect.width) * 100;
      pct = Math.max(20, Math.min(70, pct));
      setChatWidthPercent(pct);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const handleDeleteIdea = async (idea) => {
    if (!idea?.id || !userId) return;
    setIsDeletingIdea(true);
    try {
      const { error: delErr } = await supabase
        .from("quick_ideas")
        .delete()
        .eq("id", idea.id)
        .eq("user_id", userId);
      if (delErr) throw delErr;
      setIdeas((prev) => prev.filter((i) => i.id !== idea.id));
      setIdeaToDelete(null);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("quick-ideas:changed", { detail: { action: "deleted", idea: { id: idea.id } } }));
      }
    } catch (err) {
      setError(err?.message || "Failed to delete idea.");
    } finally {
      setIsDeletingIdea(false);
    }
  };

  const availableTagOptions = Array.from(
    new Set(ideas.flatMap((idea) => (Array.isArray(idea.tags) ? idea.tags : [])).filter(Boolean))
  ).sort().map((tag) => ({ value: tag, label: tag }));

  const selectedTagValues = selectedTags.map((t) => t.value.toLowerCase());

  const filteredIdeas = ideas.filter((idea) => {
    const tags = Array.isArray(idea.tags) ? idea.tags : [];
    const matchesSearch = !searchTerm.trim() ||
      [idea.title || "", stripHtml(idea.description || ""), ...tags].join(" ").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTags = selectedTagValues.length === 0 ||
      tags.some((t) => selectedTagValues.includes(t.toLowerCase()));
    return matchesSearch && matchesTags;
  });

  const hasActiveFilters = Boolean(searchTerm || selectedTags.length);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="task-popup export-popup ideas-expand-popup"
    >
      <Modal.Header closeButton>
        <Modal.Title>Ideas</Modal.Title>
      </Modal.Header>

      <Modal.Body
        ref={modalBodyRef}
        className="export-more"
        style={{ overflow: "visible", display: "flex", flexDirection: "row", height: "70vh", padding: 0 }}
      >
        {/* Resize Handle */}
        {!isChatMinimized && (
          <div
            onMouseDown={handleResizeStart}
            style={{
              width: "14px",
              height: "100%",
              background: "var(--border-color, #d5e5e3)",
              cursor: "col-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              order: 1,
            }}
          >
            <div style={{ width: "4px", height: "40px", borderRadius: "2px", background: "var(--teal-bg, #14b8a6)", opacity: 0.5 }} />
          </div>
        )}

        {/* Chat - RIGHT SIDE */}
        <div
          style={{
            width: isChatMinimized ? "0px" : `${chatWidthPercent}%`,
            minWidth: isChatMinimized ? "0px" : "280px",
            maxWidth: isChatMinimized ? "0px" : "70%",
            height: "100%",
            transition: isResizing ? "none" : "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            position: "relative",
            background: isChatMinimized ? "transparent" : "#FBF9FA",
            overflow: "visible",
            order: 2,
          }}
        >
          <TaskAssistantChat
            ref={chatAssistantRef}
            isDarkMode={isDarkMode}
            onToggle={(minimized) => setIsChatMinimized(minimized)}
            showInput={false}
            externalInput={chatInput}
            onExternalInputChange={setChatInput}
            contextType="ideas"
            userId={userId}
            onItemsUpdated={fetchIdeas}
          />
        </div>

        {/* Ideas List - LEFT SIDE */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            overflowY: "auto",
            padding: "16px",
            order: 0,
          }}
        >
          {/* Toolbar */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <button
              className="btn btn-sm"
              style={{ background: "var(--teal-bg)", color: "#fff", borderRadius: "8px", flexShrink: 0 }}
              onClick={() => { setIdeaToEdit(null); setShowAddIdeaModal(true); }}
            >
              <i className="bi bi-plus-lg"></i> Add
            </button>
            <span style={{ fontSize: "0.8rem", color: "var(--text)", opacity: 0.6, flexShrink: 0 }}>
              {filteredIdeas.length} idea{filteredIdeas.length !== 1 ? "s" : ""}
            </span>
            {hasActiveFilters && (
              <button
                type="button"
                className="btn btn-sm"
                style={{ fontSize: "0.75rem", opacity: 0.7, flexShrink: 0 }}
                onClick={() => { setSearchTerm(""); setSelectedTags([]); }}
                title="Clear filters"
              >
                <i className="bi bi-x-lg"></i> Clear
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <Select
              isMulti
              isSearchable
              isClearable={false}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              placeholder="Filter by tags"
              className="popup-select-container"
              classNamePrefix="popup-select"
              options={availableTagOptions}
              value={selectedTags}
              onChange={(opts) => setSelectedTags(Array.isArray(opts) ? opts : [])}
              styles={{ container: (base) => ({ ...base, flex: 1, minWidth: 0 }) }}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search ideas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>

          {/* Loading / Error */}
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" style={{ color: "#14b8a6" }} />
            </div>
          )}
          {error && <div className="text-center py-3" style={{ color: "#ef4444", fontSize: "0.85rem" }}>{error}</div>}

          {/* Ideas Cards */}
          {!loading && !error && filteredIdeas.length === 0 && (
            <div className="text-center py-4" style={{ fontSize: "0.85rem", opacity: 0.6 }}>
              {ideas.length === 0 ? "No ideas saved yet." : "No ideas match your search."}
            </div>
          )}

          {!loading &&
            !error &&
            filteredIdeas.map((idea) => {
              const tags = Array.isArray(idea.tags) ? idea.tags.filter(Boolean) : [];
              const attachCount = Array.isArray(idea.attachment_urls) ? idea.attachment_urls.filter(Boolean).length : 0;
              const preview = stripHtml(idea.description || "").slice(0, 120);

              return (
                <div
                  key={idea.id}
                  className="task-card ideas-sidebar__card"
                  style={{ cursor: "pointer", marginBottom: "12px" }}
                  onClick={() => { setIdeaToEdit(idea); setShowAddIdeaModal(true); }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <i className="bi bi-lightbulb" style={{ color: "var(--teal-bg)", fontSize: "0.9rem" }}></i>
                        <h4
                          className="ideas-sidebar__card-title"
                          style={{ margin: 0, fontSize: "0.88rem", fontWeight: 600 }}
                        >
                          {idea.title || "Untitled Idea"}
                        </h4>
                      </div>

                      {preview && (
                        <p style={{ margin: "4px 0 0 26px", fontSize: "0.78rem", opacity: 0.7, lineHeight: 1.4 }}>
                          {preview}{preview.length >= 120 ? "..." : ""}
                        </p>
                      )}

                      {tags.length > 0 && (
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginTop: "6px", paddingLeft: "26px" }}>
                          {tags.map((tag) => (
                            <span
                              key={`${idea.id}-${tag}`}
                              className="ideas-sidebar__inline-tag"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {attachCount > 0 && (
                        <div style={{ marginTop: "6px", paddingLeft: "26px", fontSize: "0.72rem", opacity: 0.5 }}>
                          <span>{attachCount} attachment{attachCount > 1 ? "s" : ""}</span>
                        </div>
                      )}
                    </div>

                    <div onClick={(e) => e.stopPropagation()}>
                      <PortalDropdown
                        align="end"
                        menuItems={[
                          { icon: "bi bi-pencil", label: "Edit", onClick: () => { setIdeaToEdit(idea); setShowAddIdeaModal(true); } },
                          { icon: "bi bi-trash", label: "Delete", danger: true, onClick: () => setIdeaToDelete(idea) },
                        ]}
                      >
                        <button type="button" className="ideas-sidebar__menu-trigger" aria-label="Actions" title="Actions">
                          <i className="bi bi-three-dots-vertical"></i>
                        </button>
                      </PortalDropdown>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 16px" }}>
        <ChatAssistantInput
          chatAssistantRef={chatAssistantRef}
          inputValue={chatInput}
          onInputChange={setChatInput}
          onClearChat={() => chatAssistantRef.current?.clearChat()}
          autoFocus={true}
        />
      </Modal.Footer>

      {/* Delete Confirmation */}
      <ConfirmationPopup
        show={Boolean(ideaToDelete)}
        handleClose={() => { if (!isDeletingIdea) setIdeaToDelete(null); }}
        title="Delete Idea"
        body={`Are you sure you want to delete "${ideaToDelete?.title || "this idea"}"? This action cannot be undone.`}
        handleConfirm={() => handleDeleteIdea(ideaToDelete)}
        isLoading={isDeletingIdea}
      />

      {/* Add/Edit Idea Popup */}
      {showAddIdeaModal && (
        <QuickIdeaPopup
          key={ideaToEdit?.id || "new-idea"}
          show={showAddIdeaModal}
          onClose={() => { setShowAddIdeaModal(false); setIdeaToEdit(null); }}
          onSaved={() => { fetchIdeas(); if (ideaToEdit) { setShowAddIdeaModal(false); setIdeaToEdit(null); } }}
          userId={userId}
          sessionId={normalizedSessionId}
          ideaToEdit={ideaToEdit}
          onDelete={async (idea) => { await handleDeleteIdea(idea); setShowAddIdeaModal(false); setIdeaToEdit(null); }}
        />
      )}
    </Modal>
  );
};

export default IdeasExpandAllPopup;
